import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import PageHero from '@/components/ui/PageHero'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSEO } from '@/hooks/useSEO'
import type { YouTubeChannel, YouTubeVideo } from '@/types'
import * as api from '@/lib/api'
import { SmartImage } from '../components/ui/SmartImage'

const SHOWCASE_PER_CHANNEL = 3

// Tailwind's JIT scans source for complete class strings, so a runtime
// `grid-cols-${n}` would never be emitted. Explicit map instead.
const GRID_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
}

function VideoThumb({ video }: { video: YouTubeVideo }) {
  return (
    <a
      href={video.youtube_url}
      target="_blank"
      rel="noopener noreferrer"
      className="group/thumb block"
    >
      <div className="relative aspect-video rounded-lg overflow-hidden border border-archive-ash/30 bg-archive-charcoal">
        <img
          src={video.thumbnail_url}
          alt={video.title}
          width={480}
          height={270}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-300 group-hover/thumb:scale-105"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-archive-black/20 opacity-0 transition-opacity group-hover/thumb:opacity-100">
          <svg className="w-9 h-9 text-white drop-shadow-lg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
      <p className="mt-1.5 text-xs text-archive-muted line-clamp-1 group-hover/thumb:text-archive-cream transition-colors">
        {video.title}
      </p>
    </a>
  )
}

function VisitChannel({ url, label }: { url: string; label: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-4 inline-flex items-center gap-1.5 text-sm text-archive-gold hover:text-archive-amber transition-colors"
    >
      {label}
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </a>
  )
}

export default function DocumentariesPage() {
  useSEO({
    title: 'Conversations',
    description: 'History explored as a conversation between two scholars of the Archive.',
    url: 'https://thearchivenetwork.com/conversations',
  })

  const [channels, setChannels] = useState<YouTubeChannel[]>([])
  const [videos, setVideos] = useState<YouTubeVideo[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([api.getYouTubeChannels(), api.getYouTubeVideos()])
      .then(([ch, vids]) => {
        setChannels(ch)
        setVideos(vids)
      })
      .catch((e) => console.error('Failed to load documentaries:', e))
      .finally(() => setLoading(false))
  }, [])

  // Showcase videos per channel (API already sorts published_at DESC).
  const videosByChannel = useMemo(() => {
    const map = new Map<string, YouTubeVideo[]>()
    for (const v of videos) {
      const arr = map.get(v.channel_id) ?? []
      if (arr.length < SHOWCASE_PER_CHANNEL) arr.push(v)
      map.set(v.channel_id, arr)
    }
    return map
  }, [videos])

  // Most-populated channels first; launching-soon (0 videos) last.
  const orderedChannels = useMemo(() => {
    return channels
      .map((c) => ({ c, n: videosByChannel.get(c.id)?.length ?? 0 }))
      .sort((a, b) => b.n - a.n)
      .map((x) => x.c)
  }, [channels, videosByChannel])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      <PageHero
        image="/images/covers/conversations.jpg"
        title="Conversations"
        subtitle="History explored as a conversation between two scholars of the Archive."
        variant="banner"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        {/* Curator mention */}
        <div className="text-center mb-12">
          <p className="text-sm text-archive-muted/90 max-w-2xl mx-auto leading-relaxed">
            Every history here is explored as a conversation between two Curators of the Archive —
            Aurelius, who reads the past through power, and Malik, who reads it through its human cost.{' '}
            <Link to="/curators" className="text-archive-gold hover:text-archive-amber transition-colors">
              Meet the Curators →
            </Link>
          </p>
        </div>

        {orderedChannels.length === 0 ? (
          <div className="text-center py-16 text-archive-muted">No channels available yet.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {orderedChannels.map((channel) => {
              const recent = videosByChannel.get(channel.id) ?? []
              const launchingSoon = recent.length === 0
              return (
                <div key={channel.id} className="artifact-frame overflow-hidden flex flex-col p-0">
                  {/* Cover hero (text-free art) */}
                  <div className="relative aspect-video overflow-hidden bg-archive-charcoal">
                    <SmartImage
                      src={channel.cover_image_url}
                      alt={channel.title}
                      sizes="(min-width: 1024px) 718px, 100vw"
                      width={718}
                      height={404}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    {launchingSoon && (
                      <div className="absolute top-3 right-3 inline-flex items-center gap-2 rounded-full border border-archive-gold/40 bg-archive-black/70 px-3 py-1.5 backdrop-blur-sm">
                        <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-archive-gold" />
                        <span className="text-[11px] uppercase tracking-wider text-archive-gold">Launching Soon</span>
                      </div>
                    )}
                  </div>

                  {/* Body */}
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-display text-xl text-archive-cream">{channel.title}</h3>

                    {launchingSoon ? (
                      <>
                        <p className="mt-2 text-sm text-archive-muted">
                          Channel launching soon — documentary episodes are on the way.
                        </p>
                        <VisitChannel url={channel.channel_url} label="Visit channel" />
                      </>
                    ) : (
                      <>
                        {/* One grid for any count: 2 videos render as 2 tiles the
                            same size as 3, rather than one stretched tile. */}
                        <div className={`mt-4 grid gap-2 ${GRID_COLS[recent.length] ?? 'grid-cols-3'}`}>
                          {recent.map((v) => (
                            <VideoThumb key={v.id} video={v} />
                          ))}
                        </div>
                        <VisitChannel url={channel.channel_url} label="New episodes weekly — Visit channel" />
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
